import { useState } from 'react';
import { Loader2, Play, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

const TABS = [
  { key: 'vat', label: 'VAT Check' },
  { key: 'margin', label: 'Margin' },
  { key: 'status', label: 'Status Dist.' },
  { key: 'currency', label: 'Currency Dist.' },
  { key: 'clients', label: 'Client Names' },
];

/* ------------------------------------------------------------------ */
/*  Tab 1: VAT Check                                                   */
/* ------------------------------------------------------------------ */

function VatCheckTab() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.brosVatCheck();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const bs = result?.booking_summary;
  const is = result?.invoice_summary;

  return (
    <div className="space-y-4">
      <Button onClick={run} disabled={loading}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
        Run
      </Button>

      {result && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {bs && (
              <div className="border rounded-lg p-3 space-y-1">
                <p className="text-sm font-medium">Booking summary</p>
                <p className="text-xs text-muted-foreground">Period from: {bs.period_start}</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <span className="text-muted-foreground">Count</span><span className="tabular-nums">{fmtInt(bs.count)}</span>
                  <span className="text-muted-foreground">Client total</span><span className="tabular-nums">{fmt(bs.total_client_total)}</span>
                  <span className="text-muted-foreground">Client ex-VAT</span><span className="tabular-nums">{fmt(bs.total_client_ex_vat)}</span>
                  <span className="text-muted-foreground">Zero-VAT count</span><span className="tabular-nums">{fmtInt(bs.zero_vat_count)}</span>
                </div>
              </div>
            )}
            {is && (
              <div className="border rounded-lg p-3 space-y-1">
                <p className="text-sm font-medium">Invoice summary</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <span className="text-muted-foreground">Count</span><span className="tabular-nums">{fmtInt(is.count)}</span>
                  <span className="text-muted-foreground">Total amount</span><span className="tabular-nums">{fmt(is.total_amount_sum)}</span>
                  <span className="text-muted-foreground">Subtotal</span><span className="tabular-nums">{fmt(is.subtotal_sum)}</span>
                  <span className="text-muted-foreground">Tax amount</span><span className="tabular-nums">{fmt(is.tax_amount_sum)}</span>
                </div>
              </div>
            )}
          </div>

          {result.samples && result.samples.length > 0 && (
            <details>
              <summary className="text-sm font-medium cursor-pointer text-muted-foreground">
                Sample bookings ({result.samples.length})
              </summary>
              <div className="mt-2 overflow-x-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1.5 font-medium">bros_id</th>
                      <th className="text-left px-2 py-1.5 font-medium">Client</th>
                      <th className="text-right px-2 py-1.5 font-medium">Client total</th>
                      <th className="text-right px-2 py-1.5 font-medium">VAT</th>
                      <th className="text-left px-2 py-1.5 font-medium">Invoices</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.samples.map((s) => (
                      <tr key={s.bros_id} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono">{s.bros_id}</td>
                        <td className="px-2 py-1">{s.client_name || '-'}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmt(s.client_total)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmt(s.vat)}</td>
                        <td className="px-2 py-1 tabular-nums">{s.linked_invoices?.length ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab 2: Margin Consistency                                          */
/* ------------------------------------------------------------------ */

function MarginTab() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.brosMarginConsistency();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const cats = result?.categories;

  return (
    <div className="space-y-4">
      <Button onClick={run} disabled={loading}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
        Run
      </Button>

      {result && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            {cats && (
              <>
                <Badge variant="outline" className="border-green-400 text-green-600">Exact: {fmtInt(cats.exact)}</Badge>
                <Badge variant="outline" className="border-amber-400 text-amber-600">Rounding: {fmtInt(cats.rounding)}</Badge>
                <Badge variant={cats.real_discrepancy > 0 ? 'destructive' : 'outline'}>
                  Real discrepancy: {fmtInt(cats.real_discrepancy)}
                </Badge>
              </>
            )}
            <span className="text-xs text-muted-foreground">
              Total: {fmtInt(result.total_bookings)} | Mean discrepancy: {result.mean_discrepancy}
            </span>
          </div>

          {cats?.real_discrepancy === 0 && (
            <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
              <CheckCircle2 className="h-4 w-4" />
              No margin discrepancies found.
            </div>
          )}

          {result.examples && result.examples.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                Discrepancy examples ({result.examples.length})
              </p>
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1.5 font-medium">bros_id</th>
                      <th className="text-right px-2 py-1.5 font-medium">client_total</th>
                      <th className="text-right px-2 py-1.5 font-medium">supplier_total</th>
                      <th className="text-right px-2 py-1.5 font-medium">margin</th>
                      <th className="text-right px-2 py-1.5 font-medium">expected</th>
                      <th className="text-right px-2 py-1.5 font-medium">discrepancy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.examples.map((e) => (
                      <tr key={e.bros_id} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono">{e.bros_id}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmt(e.client_total)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmt(e.supplier_total)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmt(e.margin)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmt(e.expected_margin)}</td>
                        <td className="px-2 py-1 text-right tabular-nums font-bold text-red-600">{fmt(e.discrepancy)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab 3: Status Distribution                                         */
/* ------------------------------------------------------------------ */

function StatusDistTab() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.brosStatusDistribution();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Button onClick={run} disabled={loading}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
        Run
      </Button>

      {result && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Bookings by status */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Bookings by status</p>
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-2 py-1.5 font-medium">Status</th>
                    <th className="text-right px-2 py-1.5 font-medium">Count</th>
                    <th className="text-right px-2 py-1.5 font-medium">Client total</th>
                  </tr>
                </thead>
                <tbody>
                  {(result.bookings || []).map((r) => (
                    <tr key={r.status} className="border-b last:border-0">
                      <td className="px-2 py-1 font-mono">{r.status}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{fmtInt(r.count)}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{fmt(r.total_client_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Invoices by status */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Invoices by status</p>
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-2 py-1.5 font-medium">Status</th>
                    <th className="text-right px-2 py-1.5 font-medium">Count</th>
                    <th className="text-right px-2 py-1.5 font-medium">Total amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(result.invoices || []).map((r) => (
                    <tr key={r.status} className="border-b last:border-0">
                      <td className="px-2 py-1 font-mono">{r.status}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{fmtInt(r.count)}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{fmt(r.total_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab 4: Currency Distribution                                       */
/* ------------------------------------------------------------------ */

function CurrencyDistTab() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.brosCurrencyDistribution();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Button onClick={run} disabled={loading}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
        Run
      </Button>

      {result && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <p className="text-sm font-medium">Bookings by currency</p>
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-2 py-1.5 font-medium">Currency</th>
                    <th className="text-right px-2 py-1.5 font-medium">Count</th>
                    <th className="text-right px-2 py-1.5 font-medium">Client total</th>
                  </tr>
                </thead>
                <tbody>
                  {(result.bookings || []).map((r) => (
                    <tr key={r.currency} className="border-b last:border-0">
                      <td className="px-2 py-1 font-mono">{r.currency}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{fmtInt(r.count)}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{fmt(r.total_client_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Invoices by currency</p>
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-2 py-1.5 font-medium">Currency</th>
                    <th className="text-right px-2 py-1.5 font-medium">Count</th>
                    <th className="text-right px-2 py-1.5 font-medium">Total amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(result.invoices || []).map((r) => (
                    <tr key={r.currency} className="border-b last:border-0">
                      <td className="px-2 py-1 font-mono">{r.currency}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{fmtInt(r.count)}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{fmt(r.total_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab 5: Client Names                                                */
/* ------------------------------------------------------------------ */

function ClientNamesTab() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.brosClientNames();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Button onClick={run} disabled={loading}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
        Run
      </Button>

      {result && (
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">
            {fmtInt(result.total_distinct_pairs)} distinct (company, client_name) pairs
          </p>

          {result.potential_duplicates && result.potential_duplicates.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Potential duplicates ({result.potential_duplicates.length})
              </p>
              <div className="space-y-1">
                {result.potential_duplicates.map((d) => (
                  <div key={d.normalized} className="border rounded-lg p-2 text-xs">
                    <span className="font-mono text-muted-foreground">{d.normalized}</span>
                    <span className="ml-2">({d.total_bookings} bookings)</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {d.variants.map((v) => (
                        <Badge key={v} variant="outline" className="text-[10px]">{v}</Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.potential_duplicates?.length === 0 && (
            <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
              <CheckCircle2 className="h-4 w-4" />
              No duplicate company names detected.
            </div>
          )}

          {result.rows && result.rows.length > 0 && (
            <details>
              <summary className="text-sm font-medium cursor-pointer text-muted-foreground">
                All pairs ({result.rows.length})
              </summary>
              <div className="mt-2 overflow-x-auto border rounded-lg max-h-80 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0">
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1.5 font-medium">Company</th>
                      <th className="text-left px-2 py-1.5 font-medium">Client name</th>
                      <th className="text-right px-2 py-1.5 font-medium">Count</th>
                      <th className="text-right px-2 py-1.5 font-medium">Client total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((r, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="px-2 py-1">{r.company || '(empty)'}</td>
                        <td className="px-2 py-1">{r.client_name || '(empty)'}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmtInt(r.count)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmt(r.total_client_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main panel                                                         */
/* ------------------------------------------------------------------ */

export function BROSDataQualityPanel() {
  const [activeTab, setActiveTab] = useState('vat');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">BR OS Data Quality</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Data quality checks across BR OS bookings and invoices.
        </p>

        {/* Tab row */}
        <div className="flex gap-1 flex-wrap">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'vat' && <VatCheckTab />}
        {activeTab === 'margin' && <MarginTab />}
        {activeTab === 'status' && <StatusDistTab />}
        {activeTab === 'currency' && <CurrencyDistTab />}
        {activeTab === 'clients' && <ClientNamesTab />}
      </CardContent>
    </Card>
  );
}
